/**
 * inspired by / based on: https://github.com/eligrey/FileSaver.js
 */

import { merge } from "./Merge";

// Detect WebView inside a native macOS app by ruling out all browsers
// We just need to check for 'Safari' because all other browsers (besides Firefox) include that too
// https://www.whatismybrowser.com/guides/the-latest-user-agent/macos
const isMacOSWebView = window.navigator
    && /Macintosh/.test(navigator.userAgent)
    && /AppleWebKit/.test(navigator.userAgent)
    && !/Safari/.test(navigator.userAgent);

type FilerSaverMethod = "anchor" | "ms" | "popup";

export interface FilerSaverSaveOptions {
    autoBom?: boolean;
    name?: string;
}

class FileSaverClass {

    private readonly mimeTypes: { [key: string]: string[] } = {
        'text/plain': ['txt'],
        'text/html': ['html', 'htm'],
        'text/css': ['css'],
        'text/javascript': ['js'],
        'application/javascript': ['js'],
        'application/json': ['json'],
        'application/ld+json': ['jsonld'],
        'application/xml': ['xml'],
        'application/pdf': ['pdf'],
        'application/zip': ['zip'],
        'application/x-rar-compressed': ['rar'],
        'image/jpeg': ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp'],
        'image/png': ['png'],
        'image/gif': ['gif'],
        'image/bmp': ['bmp'],
        'image/webp': ['webp'],
        'image/svg+xml': ['svg', 'svgz'],
        'video/mp4': ['mp4', 'm4v'],
        'video/x-matroska': ['mkv'],
        'video/quicktime': ['mov'],
        'video/x-msvideo': ['avi'],
        'video/webm': ['webm'],
        'audio/mpeg': ['mp3', 'mpeg', 'mpga'],
        'audio/ogg': ['ogg', 'opus'],
        'audio/wav': ['wav'],
        'audio/webm': ['weba'],
        'application/msword': ['doc', 'docx'],
        'application/vnd.ms-excel': ['xls', 'xlsx'],
        'application/vnd.ms-powerpoint': ['ppt', 'pptx'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
        'application/vnd.oasis.opendocument.text': ['odt'],
        'application/vnd.oasis.opendocument.spreadsheet': ['ods'],
        'application/vnd.oasis.opendocument.presentation': ['odp'],
        'application/vnd.apple.installer+xml': ['mpkg'],
        'application/x-shockwave-flash': ['swf'],
    };

    public get method(): FilerSaverMethod {
        if ('download' in HTMLAnchorElement.prototype && !isMacOSWebView) {
            return "anchor";
        }
        else if ('msSaveOrOpenBlob' in navigator) {
            return "ms";
        }
        else {
            return "popup";
        }
    }

    public saveUrl(url: string, options: FilerSaverSaveOptions = {}) {
        const method = this.method;

        if (method === "anchor") {
            const a = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement;
            a.download = options.name;
            a.rel = 'noopener'; // tabnabbing
            a.href = url
            if (a.origin !== location.origin) {
                if (this.corsEnabled(a.href)) {
                    this.download(url, options);
                } else {
                    a.target = '_blank';
                    this.click(a);
                }
            } else {
                this.click(a);
            }
        }
        else if (method === "ms") {
            if (this.corsEnabled(url)) {
                this.download(url, options);
            } else {
                var a = document.createElement('a')
                a.href = url;
                a.target = '_blank';
                setTimeout(() => { this.click(a) });
            }
        }
        else if (method === "popup") {
            return this.download(url, options);
        }
    }

    public saveString(text: string, options: FilerSaverSaveOptions = {}) {
        return this.saveBlob(
            new Blob([text], {
                type: 'text/plain'
            }), options);
    }

    public saveFile(file: File, options: FilerSaverSaveOptions = {}) {
        return this.saveBlob(file, options);
    }

    public saveBlob(blob: Blob, options: FilerSaverSaveOptions = {}) {
        options = merge({
            autoBom: false,
            name: "download"
        }, options);

        const method = this.method;
        const name = options.name;

        //Use download attribute first if possible (#193 Lumia mobile) unless this is a macOS WebView
        if (method === "anchor") {
            const URL = window.URL || window.webkitURL;

            // Namespace is used to prevent conflict w/ Chrome Poper Blocker extension (Issue #561)
            const a = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement;
            a.download = name;
            a.rel = 'noopener';
            a.href = URL.createObjectURL(blob);
            setTimeout(() => { URL.revokeObjectURL(a.href) }, 30 * 1000);
            setTimeout(() => { this.click(a) }, 0);
        }
        else if (method === "ms") {
            (navigator as any).msSaveOrOpenBlob(this.bom(blob, options), name);
        }
        else if (method === "popup") {
            // Fallback to using FileReader and a popup

            // Open a popup immediately do go around popup blocker
            // Mostly only available on user interaction and the fileReader is async so...
            let popup = open('', '_blank');
            if (popup) {
                popup.document.title
                    = popup.document.body.innerText
                    = 'downloading...';
            }


            const force = blob.type === 'application/octet-stream';
            const isSafari = /constructor/i.test(window.HTMLElement + "") || window.safari;
            const isChromeIOS = /CriOS\/[\d]+/.test(navigator.userAgent);

            if (
                (isChromeIOS || (force && isSafari) || isMacOSWebView)
                && typeof FileReader !== 'undefined') {
                // Safari doesn't allow downloading of blob URLs
                const reader = new FileReader()
                reader.onloadend = function () {
                    var url = reader.result as string;
                    url = isChromeIOS ? url : url.replace(/^data:[^;]*;/, 'data:attachment/file;');
                    if (popup) {
                        popup.location.href = url;
                    }
                    else {
                        window.location.href = url;
                    }
                    popup = null; // reverse-tabnabbing #460
                }
                reader.readAsDataURL(blob);
            } else {
                const URL = window.URL || window.webkitURL;
                const url = URL.createObjectURL(blob);
                if (popup) {
                    popup.location = url;
                }
                else {
                    location.href = url;
                }
                popup = null; // reverse-tabnabbing #460
                setTimeout(() => { URL.revokeObjectURL(url) }, 30 * 1000);
            }
        }
    }

    private bom(blob: Blob, opts: FilerSaverSaveOptions) {
        // prepend BOM for UTF-8 XML and text/* types (including HTML)
        // note: your browser will automatically convert UTF-16 U+FEFF to EF BB BF
        if (opts.autoBom && /^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
            return new Blob([String.fromCharCode(0xFEFF), blob], { type: blob.type })
        }
        return blob
    }

    private download(url: string, options: FilerSaverSaveOptions) {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.onload = () => {
            this.saveBlob(xhr.response, options)
        }
        xhr.onerror = () => {
            console.error('could not download file')
        }
        xhr.send();
    }

    private corsEnabled(url: string) {
        const xhr = new XMLHttpRequest()
        // use sync to avoid popup blocker
        xhr.open('HEAD', url, false)
        try {
            xhr.send()
        } catch (e) { }
        return xhr.status >= 200 && xhr.status <= 299
    }

    // `a.click()` doesn't work for all browsers (#465)
    private click(node: Node) {
        try {
            node.dispatchEvent(new MouseEvent('click'))
        } catch (e) {
            var evt = document.createEvent('MouseEvents')
            evt.initMouseEvent('click', true, true, window, 0, 0, 0, 80,
                20, false, false, false, false, 0, null)
            node.dispatchEvent(evt)
        }
    }
}

export const fileSaver = new FileSaverClass();